import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  Switch,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useThemeStore } from '../store/themeStore';
import { colors } from '../utils/colors';
import { typography } from '../utils/typography';
import { spacing } from '../utils/theme';

type ThemeMode = 'light' | 'dark' | 'system';
type FontSize = 'small' | 'medium' | 'large';

export const DisplaySettingsScreen: React.FC = () => {
  const navigation = useNavigation();
  const { mode, setTheme } = useThemeStore();
  const [fontSize, setFontSize] = useState<FontSize>('medium');
  const [autoPlayVideos, setAutoPlayVideos] = useState(false);
  const [loadImages, setLoadImages] = useState(true);
  const [reduceMotion, setReduceMotion] = useState(false);

  const themeOptions: { value: ThemeMode; label: string; icon: string }[] = [
    { value: 'light', label: 'ライトモード', icon: 'sunny' },
    { value: 'dark', label: 'ダークモード', icon: 'moon' },
    { value: 'system', label: 'システム設定', icon: 'phone-portrait' },
  ];

  const fontSizeOptions: { value: FontSize; label: string }[] = [
    { value: 'small', label: '小' },
    { value: 'medium', label: '中（標準）' },
    { value: 'large', label: '大' },
  ];

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>表示設定</Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView>
        {/* テーマセクション */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>テーマ</Text>

          {themeOptions.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={styles.option}
              onPress={() => setTheme(option.value)}
            >
              <View style={styles.optionLeft}>
                <Ionicons
                  name={option.icon as any}
                  size={22}
                  color={colors.textSecondary}
                />
                <Text style={styles.optionLabel}>{option.label}</Text>
              </View>
              {mode === option.value && (
                <Ionicons name="checkmark" size={22} color={colors.primary} />
              )}
            </TouchableOpacity>
          ))}
        </View>

        {/* フォントサイズセクション */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>フォントサイズ</Text>
          <Text style={styles.sectionDescription}>
            投稿やプロフィールのテキストサイズを変更します
          </Text>

          {fontSizeOptions.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={styles.option}
              onPress={() => setFontSize(option.value)}
            >
              <Text style={styles.optionLabel}>{option.label}</Text>
              {fontSize === option.value && (
                <Ionicons name="checkmark" size={22} color={colors.primary} />
              )}
            </TouchableOpacity>
          ))}
        </View>

        {/* メディア設定セクション */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>メディア</Text>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>画像を自動読み込み</Text>
              <Text style={styles.settingDescription}>
                オフにするとデータ通信量を節約できます
              </Text>
            </View>
            <Switch
              value={loadImages}
              onValueChange={setLoadImages}
              trackColor={{ false: colors.border, true: colors.primaryLight }}
              thumbColor={loadImages ? colors.primary : colors.textSecondary}
            />
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>動画を自動再生</Text>
              <Text style={styles.settingDescription}>
                タイムライン上で動画を自動的に再生します（今後対応予定）
              </Text>
            </View>
            <Switch
              value={autoPlayVideos}
              onValueChange={setAutoPlayVideos}
              trackColor={{ false: colors.border, true: colors.primaryLight }}
              thumbColor={autoPlayVideos ? colors.primary : colors.textSecondary}
              disabled={true}
            />
          </View>
        </View>

        {/* アクセシビリティセクション */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>アクセシビリティ</Text>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>視差効果を減らす</Text>
              <Text style={styles.settingDescription}>
                アニメーションやモーション効果を最小限にします
              </Text>
            </View>
            <Switch
              value={reduceMotion}
              onValueChange={setReduceMotion}
              trackColor={{ false: colors.border, true: colors.primaryLight }}
              thumbColor={reduceMotion ? colors.primary : colors.textSecondary}
            />
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background,
  },
  backButton: {
    padding: 4,
    width: 32,
  },
  headerTitle: {
    ...typography.h2,
    color: colors.text,
    flex: 1,
    textAlign: 'center',
  },
  headerSpacer: {
    width: 32,
  },
  section: {
    marginTop: spacing.lg,
    paddingHorizontal: spacing.md,
  },
  sectionTitle: {
    ...typography.h4,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  sectionDescription: {
    ...typography.caption,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  optionLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  optionLabel: {
    ...typography.body,
    color: colors.text,
    marginLeft: spacing.md,
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  settingInfo: {
    flex: 1,
    marginRight: spacing.md,
  },
  settingLabel: {
    ...typography.body,
    color: colors.text,
    marginBottom: 4,
  },
  settingDescription: {
    ...typography.caption,
    color: colors.textSecondary,
  },
});
